/**
 * **The one boundary between the grid's address draft and `AddressSearch`'s
 * address** (LF-6303 / [ATTR-2]).
 *
 * The shared grid carries `PostalAddress` in its {@link AttributeDraft} union
 * (see that type's doc for why that direction, and not the prototype's
 * `StructuredAddress`). `AddressSearch` — the lookup a contact's address is
 * picked from, which a lead's five bare inputs do not have — speaks
 * `StructuredAddress`. The two disagree about exactly one thing: whether a part
 * that has not been filled in yet is `undefined` or `''`.
 *
 * That is a one-line disagreement, which is precisely why it needs a named home.
 * Written out at each call site it becomes five `?? ''`s that every future
 * adopter re-derives and one of them gets wrong — and a dropped part in an
 * address is a letter that does not arrive. Root `CLAUDE.md` § *"Bind to the
 * source of truth"*.
 *
 * ## Why the conversion only runs one way
 *
 * `StructuredAddress` requires the five parts and `PostalAddress` merely permits
 * them, so a `StructuredAddress` **is already** a valid `PostalAddress` and
 * needs no function to become one — assign it. Only the widening direction
 * (draft → editor) has anything to do, which is {@link toStructuredAddress}.
 * {@link toAddressDraft} exists for the other half of the round trip: taking
 * what the editor hands back and dropping the provider's UI-only
 * `formattedAddress` before it is stored.
 *
 * ## What this deliberately does NOT do
 *
 * It does not convert to or from `Contact['address']`. That shape is a third
 * one — it adds `businessName` and `poBox`, and every member is `| null` rather
 * than `| undefined` — so it is assignable to neither type here. Mapping it is
 * [ATTR-4]'s `toPatch`, which must **preserve the sibling keys it does not
 * own**: the production precedent is `components/contacts/contact-form/
 * AddressSection.tsx`, which spreads the existing address and overwrites only
 * the five parts, exactly so `businessName` / `poBox` survive an address edit.
 */

import type { StructuredAddress } from '@/components/shared/address-search';
import type { PostalAddress } from '@/lib/services/matters/types';

/**
 * The five parts both shapes agree on, in the order they are written on an
 * envelope.
 *
 * Derived from the two types' own keys via `satisfies`, so a part added to
 * either one cannot be silently missing from this boundary — the compiler names
 * it. The same guard `attribute-address-editor`'s `ADDRESS_PARTS` uses, and for the
 * same reason.
 */
const SHARED_ADDRESS_PARTS = [
  'streetAddress',
  'suburb',
  'state',
  'postcode',
  'country'
] as const satisfies readonly (keyof PostalAddress & keyof StructuredAddress)[];

/**
 * Widen a grid draft into the address `AddressSearch` requires.
 *
 * An absent part becomes `''` rather than being omitted, which is the whole
 * difference between the two shapes. `null` — the draft an address field holds
 * when nothing is captured — widens to five empty strings, so opening the
 * lookup on an empty address shows five empty boxes rather than throwing.
 *
 * `formattedAddress` is deliberately not synthesised. It is the *provider's*
 * single-line string, populated on a search selection and cleared on manual
 * edit; inventing one from parts we already hold would claim a lookup happened
 * when it did not.
 */
export function toStructuredAddress(
  draft: PostalAddress | null | undefined
): StructuredAddress {
  return {
    streetAddress: draft?.streetAddress ?? '',
    suburb: draft?.suburb ?? '',
    state: draft?.state ?? '',
    postcode: draft?.postcode ?? '',
    country: draft?.country ?? ''
  };
}

/**
 * Narrow what `AddressSearch` hands back into a grid draft.
 *
 * Two things happen here, and both are the reason this is not just an
 * assignment:
 *
 * · **`formattedAddress` is dropped.** It is UI-only — the provider's own
 *   single-line rendering — and storing it would put a second, staler copy of
 *   the address beside the parts that are the actual record.
 * · **An address whose every part is blank becomes `null`, not five empty
 *   strings.** Otherwise an *"is anything captured here?"* read sees an object
 *   and answers yes. This matches `attribute-address-editor`'s own `normalise`, so
 *   the two paths cannot disagree about what an emptied address is.
 */
export function toAddressDraft(
  address: StructuredAddress | null | undefined
): PostalAddress | null {
  if (!address) return null;

  const draft: PostalAddress = {
    streetAddress: address.streetAddress,
    suburb: address.suburb,
    state: address.state,
    postcode: address.postcode,
    country: address.country
  };

  const anyPart = SHARED_ADDRESS_PARTS.some(
    (part) => (draft[part] ?? '').trim().length > 0
  );
  return anyPart ? draft : null;
}
